import React, { useRef } from 'react';
import {
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  HandRaisedIcon,
  DocumentTextIcon,
  ChatBubbleLeftEllipsisIcon,
  SparklesIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

/**
 * Step 2 of the Qualifying Agent flow: what the agent says and asks, in order.
 *
 *   1. Intro       — who is calling, about which role, are you interested in applying. Always a
 *                    knockout. The call puts "Hi {first name}!" in front of it (server spokenLines.js).
 *   2. Role brief  — the pitch: the company, the role and where, the work, ending "Does this
 *                    role sound exciting to you?". Written by the server from the job post
 *                    (roleBriefWriter.js, `briefTools.onRewrite`). Always a knockout. Played only
 *                    when switched on (`roleBriefEnabled`, off for a new screening since 22 Oct);
 *                    its text is kept while off.
 *   3. The recruiter's own questions — none prefilled. Each can be made a knockout and/or
 *      mandatory (both off by default).
 *
 * Mandatory: the intro always (28 Oct, fixed like its knockout), the recruiter's questions when
 * marked. Once every mandatory question has an answer, the candidate is not called again; the
 * rest go to WhatsApp (call + WhatsApp) or stay unanswered (call only). Questions are still asked
 * in the order shown (server: callSchedule.js isMandatoryQuestion / mandatoryAnswered / decideRecall).
 *
 * Knockout: the expected answer becomes a must-have requirement, written in plain English for a
 * number or short answer ("Notice period of 31 days or less"). On the call, an answer that clearly
 * does not meet it ends the call politely; anything unclear never does — the call goes on and the
 * score decides (server: knockoutRequirement.js, voice/conversationMode.js).
 *
 * Screenings made before this layout keep their saved questions (salary, notice period, …);
 * they show as the recruiter's own questions and keep their kind, so scoring is unchanged.
 *
 * Props:
 *  questions  — [{ id, text, kind, knockout, answerType?, expectedAnswer? }]
 *  onChange   — setter for the whole array
 *  details    — { orgName, role } from step 1, to show {{company}}/{{role}} as real names
 *  briefTools — { writing, stale, onRewrite } from the modal, which owns the brief request
 *  roleBriefEnabled, onRoleBriefToggle — the call plays the brief; the modal owns the switch
 *  channelMode — 'call' | 'whatsapp' | 'both', for what "mandatory" means on this screening
 */

// Mirrors KNOCKOUT_KINDS in server/modules/qualifying-agent/qualifyingScore.js — the default for
// questions saved before the per-question switch existed.
const KNOCKOUT_BY_KIND = new Set(['intro', 'willingness', 'ai_interview_consent']);

export const isKnockout = (q) => (typeof q.knockout === 'boolean' ? q.knockout : KNOCKOUT_BY_KIND.has(q.kind));
/** The intro is always mandatory; the recruiter's questions when marked (server: callSchedule.isMandatoryQuestion). */
export const isMandatory = (q) => q.kind === 'intro' || q.mandatory === true;

// Mirrors DEFAULT_INTRO_TEXT / LEGACY_DEFAULT_INTROS / AGENT_NAMES in
// server/modules/qualifying-agent/opening.js. {{agent}} is the voice's name (step 1's voice);
// delete it from the intro and the call has no name anywhere.
// 22 Oct: the intro asks whether they are interested in applying, not "two minutes to talk?".
export const INTRO_TEXT =
  "I'm {{agent}} from {{company}}. We have an opening for the {{role}} job role. Are you interested in applying?";
const LEGACY_INTROS = new Set([
  "I'm calling on behalf of {{company}} about the {{role}} role. Are you free to talk for two minutes?",
  'This is the hiring team at {{company}}, calling about the {{role}} role. Do you have two minutes to talk?',
  'This is {{agent}} from the hiring team at {{company}}, calling about the {{role}} role. Do you have two minutes to talk?',
  'This is {{agent}} from {{company}}. We have an opening for the {{role}} job role. Do you have two minutes to talk?',
  'This is {{agent}}, calling on behalf of {{company}} about a {{role}} role that may suit your profile. Do you have two minutes to talk?',
]);
export const AGENT_NAMES = { female: 'Neha', male: 'Raj' };
export const agentNameFor = (voiceGender) => AGENT_NAMES[voiceGender] || AGENT_NAMES.female;
/** A saved intro still on an old default was never edited: show (and save) today's wording. */
export const currentIntroText = (text) => (LEGACY_INTROS.has(String(text || '').trim()) ? INTRO_TEXT : text);
// Mirrors INTEREST_QUESTION in server/modules/qualifying-agent/roleBriefWriter.js.
export const BRIEF_QUESTION = 'Does this role sound exciting to you?';

// The call speaks at about 140 words a minute (Cartesia speed 0.9). Past ~40 s candidates start
// talking over the agent (29 Sep calls).
const spokenSeconds = (text) => Math.round((String(text || '').split(/\s+/).filter(Boolean).length / 140) * 60);
const LONG_BRIEF_SECONDS = 40;

/**
 * Real names in the editor → {{company}}/{{role}} in what is saved, so a later rename flows through.
 * `agentName` (the intro only): the voice's name as a whole word → {{agent}}, so a voice change
 * renames it ("Raj" inside "Rajasthan" stays).
 */
export function toStoredText(text, { orgName = '', role = '', agentName = '' } = {}) {
  let raw = String(text || '');
  if (orgName.trim()) raw = raw.split(orgName.trim()).join('{{company}}');
  if (role.trim()) raw = raw.split(role.trim()).join('{{role}}');
  if (agentName) raw = raw.replace(new RegExp(`\\b${agentName}\\b`, 'g'), '{{agent}}');
  return raw;
}

/** The prefilled role brief: the first two sentences of the job post, then the interest question. */
export function defaultBrief(jobDescription) {
  const clean = String(jobDescription || '').replace(/[#*_`]/g, ' ').replace(/\s+/g, ' ').trim();
  const sentences = (clean.match(/[^.!?]+[.!?]+/g) || (clean ? [clean] : []))
    .slice(0, 2)
    .map((s) => s.trim())
    .join(' ');
  const short = sentences.length > 240 ? `${sentences.slice(0, 240).replace(/\s+\S*$/, '')}.` : sentences;
  return short ? `${short} ${BRIEF_QUESTION}` : `We have an opening for the {{role}} role. ${BRIEF_QUESTION}`;
}

/**
 * What a complete answer looks like, the same idea as the AI interview's question type. The call
 * uses it to know when to follow up ("as per market" is not a number); the scorer compares the
 * answer with `expectedAnswer`. On a knockout question the call checks the answer against it too;
 * the agent never says it to the candidate.
 */
export const ANSWER_TYPES = [
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'number', label: 'Number' },
  { value: 'short', label: 'Short answer' },
];

const EXPECTED_PLACEHOLDER = {
  number: 'e.g. 2 years or more · under ₹30,000 a month',
  short: 'e.g. Lives within 10 km of Andheri',
};

// On a knockout the expected answer is the requirement: one plain-English sentence.
const REQUIREMENT_PLACEHOLDER = {
  number: 'e.g. Notice period of 31 days or less · between 3 and 6 years',
  short: 'e.g. Lives within 10 km of Andheri · has a B.Com degree',
};

/** A first guess from the wording, until the recruiter picks a type. */
export function guessAnswerType(text) {
  const t = String(text || '').trim();
  if (/^(are|is|am|do|does|did|can|could|will|would|have|has|had|should|shall|may|were|was)\b/i.test(t)) return 'yes_no';
  if (/\bhow (many|much|long|soon|old)\b|\b(salary|ctc|stipend|notice period|in-hand)\b/i.test(t)) return 'number';
  return 'short';
}

export const answerTypeOf = (q) => q.answerType || guessAnswerType(q.text);

/** Yes / No questions always have an expected answer (default Yes); the rest are optional. */
export const expectedAnswerOf = (q) =>
  answerTypeOf(q) === 'yes_no' ? (/^\s*n/i.test(q.expectedAnswer || '') ? 'No' : 'Yes') : String(q.expectedAnswer || '').trim();

/**
 * Why this question cannot be saved, or '' — the same rule as the server's
 * (knockoutRequirement.js knockoutProblem): a knockout needs the requirement it checks.
 */
export function knockoutProblemOf(q) {
  if (!isKnockout(q) || q.kind === 'intro' || q.kind === 'willingness') return '';
  return expectedAnswerOf(q) ? '' : 'Write the requirement in Expected answer: a knockout needs it.';
}

const newQuestion = () => ({
  id: `qa_custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  text: '',
  kind: 'custom',
  knockout: false,
  mandatory: false,
  expectedAnswer: '',
});

export default function QualifyingAgentQuestionsEditor({
  questions = [],
  onChange,
  details = {},
  briefTools = {},
  roleBriefEnabled = false,
  onRoleBriefToggle,
  channelMode = 'both',
}) {
  const orgName = details.orgName?.trim() || '';
  const role = details.role?.trim() || '';
  const lastCardRef = useRef(null);

  // Intro and brief are pinned to the top in that order; everything else is the recruiter's.
  const intro = questions.find((q) => q.kind === 'intro');
  const brief = questions.find((q) => q.kind === 'willingness');
  const own = questions.filter((q) => q !== intro && q !== brief);
  // Numbered as the call asks them: the intro, the brief when it is on, then the recruiter's.
  const ownStartNum = 2 + (roleBriefEnabled && brief ? 1 : 0);
  const mandatoryNote = MANDATORY_NOTE[channelMode] || MANDATORY_NOTE.both;

  const emit = (nextOwn, patch = {}) =>
    onChange(
      [
        intro && { ...intro, ...(patch.intro || {}) },
        brief && { ...brief, ...(patch.brief || {}) },
        ...nextOwn,
      ].filter(Boolean)
    );

  // Shown with the real names so the recruiter reads the actual sentence; saved with the
  // placeholders so a later company or role change still flows through.
  const agentName = agentNameFor(details.voiceGender);
  const show = (text) =>
    String(text || '')
      .replace(/\{\{role\}\}/gi, role || '{{role}}')
      .replace(/\{\{company\}\}/gi, orgName || '{{company}}')
      .replace(/\{\{agent\}\}/gi, agentName);
  const store = (text) => toStoredText(text, { orgName, role });
  const storeIntro = (text) => toStoredText(text, { orgName, role, agentName });

  const updateOwn = (i, patch) => emit(own.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i, delta) => {
    const t = i + delta;
    if (t < 0 || t >= own.length) return;
    const next = [...own];
    [next[i], next[t]] = [next[t], next[i]];
    emit(next);
  };
  const add = () => {
    emit([...own, newQuestion()]);
    requestAnimationFrame(() => {
      lastCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      lastCardRef.current?.querySelector('textarea')?.focus();
    });
  };

  const blanks = own.filter((q) => !q.text.trim()).length;

  return (
    <div className="w-full space-y-5">
      {/* ── Opening ─────────────────────────────────────────────────────── */}
      <section className="space-y-2.5">
        <SectionTitle title="How the call opens" sub="The intro is prefilled — edit the wording if you like. The role brief is optional." />
        {intro && (
          <FixedCard
            step={1}
            Icon={HandRaisedIcon}
            title="Intro"
            prefix="Hi Priya!"
            hint="Who is calling, for which role, and whether they are interested in applying."
            value={show(intro.text)}
            onChange={(v) => emit(own, { intro: { text: storeIntro(v), knockout: true } })}
            testId="qa-intro-text"
            mandatory
          />
        )}
        {(brief || onRoleBriefToggle) && (
          <div className="space-y-2">
            {onRoleBriefToggle && <RoleBriefSwitch on={roleBriefEnabled} onChange={onRoleBriefToggle} />}
            {roleBriefEnabled && brief && (
              <FixedCard
                step={2}
                Icon={DocumentTextIcon}
                title="Role brief"
                hint={<BriefHint text={show(brief.text)} stale={briefTools.stale} />}
                value={show(brief.text)}
                onChange={(v) => emit(own, { brief: { text: store(v), knockout: true } })}
                rows={5}
                testId="qa-brief-text"
                busy={briefTools.writing ? 'Writing a role brief from the job post…' : ''}
                action={
                  briefTools.onRewrite && (
                    <button
                      type="button"
                      onClick={briefTools.onRewrite}
                      disabled={briefTools.writing}
                      data-testid="qa-brief-rewrite"
                      className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#007BFF] transition-colors hover:bg-[#007BFF]/10 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <SparklesIcon className="h-3.5 w-3.5" />
                      {briefTools.writing ? 'Writing…' : 'Rewrite with AI'}
                    </button>
                  )
                }
              />
            )}
          </div>
        )}
      </section>

      {/* ── The recruiter's questions ───────────────────────────────────── */}
      <section className="space-y-2.5">
        <div className="flex items-end justify-between gap-3">
          <SectionTitle title="Your questions" sub={`Asked in this order, after the ${roleBriefEnabled ? 'role brief' : 'intro'}.`} />
          <button
            type="button"
            onClick={add}
            data-testid="qa-add-question-btn"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5 hover:text-[#007BFF]"
          >
            <PlusIcon className="h-4 w-4" />
            Add question
          </button>
        </div>

        {own.length === 0 ? (
          <button
            type="button"
            onClick={add}
            className="flex w-full flex-col items-center rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center transition-colors hover:border-[#007BFF]/50 hover:bg-[#007BFF]/5"
          >
            <ChatBubbleLeftEllipsisIcon className="h-6 w-6 text-gray-300" />
            <span className="mt-1.5 text-sm font-medium text-gray-700">Add your first question</span>
            <span className="mt-0.5 text-xs text-gray-500">e.g. “Can you join within 15 days?” or “Do you have a two-wheeler?”</span>
          </button>
        ) : (
          own.map((q, i) => {
            const knockout = isKnockout(q);
            const type = answerTypeOf(q);
            const expected = expectedAnswerOf(q);
            const problem = knockoutProblemOf(q);
            const knockoutNote = !knockout
              ? 'Recorded and scored — never rejects on its own.'
              : problem
                ? null
                : type === 'yes_no'
                  ? `A clear “${expected === 'Yes' ? 'No' : 'Yes'}” ends the call politely.`
                  : 'An answer that clearly does not meet the expected answer ends the call politely.';
            return (
              <div
                key={q.id}
                ref={i === own.length - 1 ? lastCardRef : null}
                data-testid="qa-question-card"
                className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors focus-within:border-[#007BFF]/40"
              >
                <div className="flex items-start gap-2.5">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-500">
                    {i + ownStartNum}
                  </span>
                  <div className="min-w-0 flex-1">
                    <textarea
                      value={q.text}
                      onChange={(e) => updateOwn(i, { text: e.target.value })}
                      rows={2}
                      placeholder="Type the question the agent should ask"
                      className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20"
                    />
                    <div className="mt-2 space-y-2 rounded-lg bg-gray-50 p-2.5">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <span className="w-24 shrink-0 text-[11px] font-medium text-gray-500">Answer type</span>
                        <div role="radiogroup" aria-label={`Answer type for question ${i + ownStartNum}`} className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                          {ANSWER_TYPES.map((t) => (
                            <button
                              key={t.value}
                              type="button"
                              role="radio"
                              aria-checked={type === t.value}
                              data-testid={`qa-answer-type-${t.value}`}
                              onClick={() => updateOwn(i, { answerType: t.value, expectedAnswer: '' })}
                              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                                type === t.value ? 'bg-[#007BFF] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <span className="w-24 shrink-0 text-[11px] font-medium text-gray-500">Expected answer</span>
                        {type === 'yes_no' ? (
                          <div role="radiogroup" aria-label={`Expected answer for question ${i + ownStartNum}`} className="flex gap-4">
                            {['Yes', 'No'].map((v) => (
                              <label key={v} className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-gray-800">
                                <input
                                  type="radio"
                                  name={`qa-expected-${q.id}`}
                                  checked={expected === v}
                                  onChange={() => updateOwn(i, { answerType: type, expectedAnswer: v })}
                                  data-testid={`qa-expected-${v.toLowerCase()}`}
                                  className="h-4 w-4 accent-[#007BFF]"
                                />
                                {v}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={q.expectedAnswer || ''}
                            maxLength={200}
                            onChange={(e) => updateOwn(i, { answerType: type, expectedAnswer: e.target.value })}
                            placeholder={(knockout ? REQUIREMENT_PLACEHOLDER : EXPECTED_PLACEHOLDER)[type]}
                            aria-required={knockout}
                            data-testid="qa-expected-text"
                            className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20"
                          />
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500">
                        {knockout && type !== 'yes_no'
                          ? 'Required for a knockout. Write it in plain English; the agent never says it to the candidate.'
                          : 'Used to score the answer. The agent never says it to the candidate.'}
                      </p>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <label
                        className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold transition-colors ${
                          knockout ? 'border-red-200 bg-red-50 text-red-600' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        title="If the answer clearly does not meet the expected answer, the call ends politely and the candidate is not taken forward."
                      >
                        <input
                          type="checkbox"
                          checked={knockout}
                          onChange={(e) => updateOwn(i, { knockout: e.target.checked })}
                          data-testid="qa-question-knockout"
                          className="h-3.5 w-3.5 rounded border-gray-300 text-red-500 focus:ring-red-200"
                        />
                        <BoltIcon className="h-3.5 w-3.5" />
                        Knockout
                      </label>
                      {knockoutNote ? (
                        <span className="text-[11px] text-gray-500">{knockoutNote}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700" data-testid="qa-knockout-needs-expected">
                          <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                          {problem}
                        </span>
                      )}
                      <label
                        className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold transition-colors ${
                          q.mandatory ? 'border-teal-200 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        title={mandatoryNote}
                      >
                        <input
                          type="checkbox"
                          checked={!!q.mandatory}
                          onChange={(e) => updateOwn(i, { mandatory: e.target.checked })}
                          data-testid="qa-question-mandatory"
                          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-200"
                        />
                        <ShieldCheckIcon className="h-3.5 w-3.5" />
                        Mandatory
                      </label>
                      <span className="ml-auto flex items-center gap-0.5">
                        <IconBtn label={`Move question ${i + ownStartNum} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                          <ChevronUpIcon className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn label={`Move question ${i + ownStartNum} down`} disabled={i === own.length - 1} onClick={() => move(i, 1)}>
                          <ChevronDownIcon className="h-4 w-4" />
                        </IconBtn>
                        <IconBtn label={`Delete question ${i + ownStartNum}`} danger onClick={() => emit(own.filter((_, j) => j !== i))}>
                          <TrashIcon className="h-4 w-4" />
                        </IconBtn>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {blanks > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            {blanks === 1 ? 'One question is empty' : `${blanks} questions are empty`} — empty questions are removed when you save.
          </div>
        )}
      </section>

      <p className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-500">
        <BoltIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />
        <span>
          <span className="font-semibold text-gray-700">Knockout:</span> a clear “no” to the intro or role brief ends
          the call politely. On your questions, an answer that clearly does not meet the expected answer ends the call
          politely too. An unclear answer never rejects anyone: the call goes on.
        </span>
      </p>
      <p className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-500">
        <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-500" />
        <span>
          <span className="font-semibold text-gray-700">Mandatory:</span> {mandatoryNote}
        </span>
      </p>
    </div>
  );
}

// ── internal ─────────────────────────────────────────────────────────────────

// What "mandatory" does, by channel (server: callSchedule.js decideRecall, handleCallEnded).
const MANDATORY_NOTE = {
  both: 'the intro is always mandatory. Once it and every question you mark are answered, the agent stops calling — the rest are asked on WhatsApp. Until then it calls again, up to the call limit.',
  call: 'the intro is always mandatory. Once it and every question you mark are answered, the agent stops calling — the rest stay unanswered and the candidate is scored on what they said. Until then it calls again, up to the call limit.',
  whatsapp: 'on WhatsApp every question is asked anyway; mandatory only matters when the agent calls.',
};

/** Whether the call plays the role brief after the intro. Off: "Great, thank you!", then question 1. */
function RoleBriefSwitch({ on, onChange }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Play the role brief"
        onClick={() => onChange(!on)}
        data-testid="qa-role-brief-toggle"
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 ${
          on ? 'bg-[#007BFF]' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`}
        />
      </button>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900">Role brief</p>
        <p className="text-[11px] text-gray-500">
          {on
            ? 'After the intro the agent tells them about the company and the role, then asks if it sounds exciting. A clear “no” ends the call.'
            : 'Off — after the intro the agent goes straight to your questions. Turn on to pitch the role first.'}
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ title, sub }) {
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {sub && <p className="text-[11px] text-gray-500">{sub}</p>}
    </div>
  );
}

/** Under the brief: how long it runs on the call, and whether step 1 changed since it was written. */
function BriefHint({ text, stale }) {
  const seconds = spokenSeconds(text);
  const long = seconds > LONG_BRIEF_SECONDS;
  return (
    <>
      Four short lines: the company, the role, the work, then “Does this role sound exciting to you?”{' '}
      <span className={long ? 'font-medium text-amber-600' : 'text-gray-400'}>
        ≈ {seconds} s on the call{long ? ' — long; candidates may talk over it' : ''}.
      </span>
      {stale && (
        <span className="mt-1 block font-medium text-amber-600">
          The role, location or salary changed since this was written — Rewrite with AI to update it.
        </span>
      )}
    </>
  );
}

function FixedCard({ step, Icon, title, hint, value, onChange, rows = 2, testId, prefix, action, busy, mandatory = false }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-colors focus-within:border-[#007BFF]/40">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#007BFF]/10 text-[11px] font-bold text-[#007BFF]">
          {step}
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center gap-2">
            <Icon className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-900">{title}</span>
            <span
              className="inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600"
              title="A clear “no” here ends the call and the candidate is not taken forward."
            >
              <BoltIcon className="h-3 w-3" />
              Knockout
            </span>
            {mandatory && (
              <span
                className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-teal-700"
                title="Always mandatory: once it and the questions you mark are answered, the agent stops calling."
                data-testid="qa-intro-mandatory"
              >
                <ShieldCheckIcon className="h-3 w-3" />
                Mandatory
              </span>
            )}
            {action}
          </div>
          {prefix && (
            <p className="mb-1 text-xs text-gray-500">
              Starts with <span className="font-medium text-gray-700">“{prefix}”</span> — the candidate’s own first name.
            </p>
          )}
          <div className="relative">
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              rows={rows}
              disabled={!!busy}
              data-testid={testId}
              className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 transition-all focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20 disabled:bg-gray-50 disabled:text-gray-400"
            />
            {busy && (
              <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-white/70 text-sm font-medium text-[#007BFF]">
                <SparklesIcon className="h-4 w-4 animate-pulse" />
                {busy}
              </div>
            )}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">{hint}</p>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ label, onClick, disabled, danger, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md p-1.5 transition-colors disabled:cursor-not-allowed disabled:text-gray-200 ${
        danger ? 'text-gray-400 hover:bg-red-50 hover:text-red-500' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}
